import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";

import {
  getAuthToken,
  getCurrentShopId,
  isLoggedIn,
} from "./auth";

import "./PromotionsPage.css";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://api.deskpos.ie";

const getTodayDate = () =>
  new Date().toISOString().slice(0, 10);

const formatMoney = (value) =>
  `€${Number(value || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return "-";

  const clean = String(value).slice(0, 10);

  const date = new Date(
    `${clean}T00:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return clean;
  }

  return date.toLocaleDateString(
    "en-IE",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );
};

const normalizeStatus = (promotion) => {
  const backendStatus = String(
    promotion?.status || ""
  )
    .trim()
    .toLowerCase();

  if (
    [
      "active",
      "upcoming",
      "expired",
      "inactive",
    ].includes(backendStatus)
  ) {
    return backendStatus;
  }

  if (
    promotion?.active === false ||
    promotion?.active === 0 ||
    promotion?.active === "false"
  ) {
    return "inactive";
  }

  const today = getTodayDate();

  const start = String(
    promotion?.start_date || ""
  ).slice(0, 10);

  const end = String(
    promotion?.end_date || ""
  ).slice(0, 10);

  if (start && today < start) {
    return "upcoming";
  }

  if (end && today > end) {
    return "expired";
  }

  return "active";
};

function MultiBuyPromotionsPage() {
  const navigate = useNavigate();

  const [shopId, setShopId] =
    useState(null);

  const [products, setProducts] =
    useState([]);

  const [promotions, setPromotions] =
    useState([]);

  const [productSearch, setProductSearch] =
    useState("");

  const [promotionSearch, setPromotionSearch] =
    useState("");

  const [selectedProducts, setSelectedProducts] =
    useState([]);

  const [
    editingPromotionId,
    setEditingPromotionId,
  ] = useState(null);

  const [form, setForm] = useState({
    name: "",
    required_qty: 2,
    bundle_price: "",
    start_date: getTodayDate(),
    end_date: getTodayDate(),
    active: true,
  });

  const [loadingProducts, setLoadingProducts] =
    useState(false);

  const [
    loadingPromotions,
    setLoadingPromotions,
  ] = useState(false);

  const [saving, setSaving] =
    useState(false);

  const [pageError, setPageError] =
    useState("");

  const [formError, setFormError] =
    useState("");

  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");

  // ======================================
  // AUTH / SHOP
  // ======================================

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/admin-login");
      return;
    }

    const currentShopId =
      getCurrentShopId();

    if (!currentShopId) {
      setPageError(
        "Shop ID was not found. Please logout and login again."
      );

      return;
    }

    setShopId(
      Number(currentShopId)
    );
  }, [navigate]);

  useEffect(() => {
    if (!shopId) return;

    fetchProducts();
    fetchPromotions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const getRequestConfig = () => {
    const token = getAuthToken();

    return {
      params: {
        shop_id: shopId,
      },

      headers: {
        Authorization:
          `Bearer ${token}`,
      },
    };
  };

  // ======================================
  // LOAD PRODUCTS
  // ======================================

  const fetchProducts = async () => {
    if (!shopId) return;

    try {
      setLoadingProducts(true);

      const response =
        await axios.get(
          `${API_BASE_URL}/products`,
          getRequestConfig()
        );

      setProducts(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(
        "Load multi-buy products error:",
        error.response?.data ||
          error.message
      );

      setProducts([]);

      setPageError(
        error.response?.data?.error ||
          "Products could not be loaded."
      );
    } finally {
      setLoadingProducts(false);
    }
  };

  // ======================================
  // LOAD PROMOTIONS
  // ======================================

  const fetchPromotions = async () => {
    if (!shopId) return;

    try {
      setLoadingPromotions(true);
      setPageError("");

      const response =
        await axios.get(
          `${API_BASE_URL}/multi-buy-promotions`,
          getRequestConfig()
        );

      setPromotions(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(
        "Load multi-buy promotions error:",
        error.response?.data ||
          error.message
      );

      setPromotions([]);

      setPageError(
        error.response?.data?.error ||
          "Multi-buy promotions could not be loaded."
      );
    } finally {
      setLoadingPromotions(false);
    }
  };

  // ======================================
  // SEARCH PRODUCTS
  // ======================================

  const filteredProducts =
    useMemo(() => {
      const cleanSearch =
        productSearch
          .trim()
          .toLowerCase();

      if (!cleanSearch) {
        return [];
      }

      return products
        .filter((product) => {
          const alreadySelected =
            selectedProducts.some(
              (selected) =>
                Number(selected.id) ===
                Number(product.id)
            );

          if (alreadySelected) {
            return false;
          }

          const name = String(
            product.name || ""
          ).toLowerCase();

          const barcode = String(
            product.barcode || ""
          ).toLowerCase();

          return (
            name.includes(cleanSearch) ||
            barcode.includes(
              cleanSearch
            )
          );
        })
        .slice(0, 30);
    }, [
      products,
      productSearch,
      selectedProducts,
    ]);

  // ======================================
  // SEARCH PROMOTIONS
  // ======================================

  const filteredPromotions =
    useMemo(() => {
      const search =
        promotionSearch
          .trim()
          .toLowerCase();

      if (!search) {
        return promotions;
      }

      return promotions.filter(
        (promotion) => {
          const name = String(
            promotion.name || ""
          ).toLowerCase();

          const productText = Array.isArray(
            promotion.products
          )
            ? promotion.products
                .map(
                  (product) =>
                    `${product.name || ""} ${
                      product.barcode || ""
                    }`
                )
                .join(" ")
                .toLowerCase()
            : "";

          return (
            name.includes(search) ||
            productText.includes(search) ||
            normalizeStatus(
              promotion
            ).includes(search)
          );
        }
      );
    }, [
      promotions,
      promotionSearch,
    ]);

  // ======================================
  // PRODUCT SELECTION
  // ======================================

  const addProduct = (product) => {
    setSelectedProducts(
      (previous) => [
        ...previous,
        product,
      ]
    );

    setProductSearch("");
    setFormError("");
  };

  const removeProduct = (productId) => {
    setSelectedProducts(
      (previous) =>
        previous.filter(
          (product) =>
            Number(product.id) !==
            Number(productId)
        )
    );

    setFormError("");
  };

  // ======================================
  // RESET FORM
  // ======================================

  const resetForm = () => {
    setEditingPromotionId(null);
    setSelectedProducts([]);
    setProductSearch("");

    setForm({
      name: "",
      required_qty: 2,
      bundle_price: "",
      start_date: getTodayDate(),
      end_date: getTodayDate(),
      active: true,
    });

    setFormError("");
  };

  // ======================================
  // VALIDATION
  // ======================================

  const validateForm = () => {
    if (!form.name.trim()) {
      return "Promotion name is required.";
    }

    const qty = Number(
      form.required_qty
    );

    if (
      !Number.isInteger(qty) ||
      qty < 2
    ) {
      return "Required quantity must be at least 2.";
    }

    if (
      selectedProducts.length < 2
    ) {
      return "Please select at least 2 eligible products.";
    }


    const bundlePrice = Number(
      form.bundle_price
    );

    if (
      !Number.isFinite(
        bundlePrice
      ) ||
      bundlePrice <= 0
    ) {
      return "Bundle price must be greater than €0.";
    }

    if (
      !form.start_date ||
      !form.end_date
    ) {
      return "Start date and end date are required.";
    }

    if (
      form.end_date <
      form.start_date
    ) {
      return "End date cannot be before start date.";
    }

    return "";
  };

  // ======================================
  // SAVE
  // ======================================

  const savePromotion = async (
    event
  ) => {
    event.preventDefault();

    const validation =
      validateForm();

    if (validation) {
      setFormError(validation);
      return;
    }

    const payload = {
      shop_id: shopId,

      name: form.name.trim(),

      required_qty: Number(
        form.required_qty
      ),

      bundle_price: Number(
        form.bundle_price
      ),

      start_date:
        form.start_date,

      end_date:
        form.end_date,

      active:
        form.active === true,

      product_ids:
        selectedProducts.map(
          (product) =>
            Number(product.id)
        ),
    };

    try {
      setSaving(true);
      setFormError("");
      setSuccessMessage("");

      const token =
        getAuthToken();

      const config = {
        params: {
          shop_id: shopId,
        },

        headers: {
          Authorization:
            `Bearer ${token}`,
        },
      };

      if (
        editingPromotionId
      ) {
        await axios.put(
          `${API_BASE_URL}/multi-buy-promotions/${editingPromotionId}`,
          payload,
          config
        );

        setSuccessMessage(
          "Multi-buy promotion updated successfully."
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/multi-buy-promotions`,
          payload,
          config
        );

        setSuccessMessage(
          "Multi-buy promotion created successfully."
        );
      }

      resetForm();

      await fetchPromotions();

      setTimeout(() => {
        setSuccessMessage("");
      }, 3500);
    } catch (error) {
      console.error(
        "Save multi-buy promotion error:",
        error.response?.data ||
          error.message
      );

      setFormError(
        error.response?.data?.error ||
          "Multi-buy promotion could not be saved."
      );
    } finally {
      setSaving(false);
    }
  };

  // ======================================
  // EDIT
  // ======================================

  const editPromotion = (
    promotion
  ) => {
    setEditingPromotionId(
      Number(promotion.id)
    );

    setForm({
      name:
        promotion.name || "",

      required_qty:
        Number(
          promotion.required_qty ||
            2
        ),

      bundle_price:
        String(
          promotion.bundle_price ??
            ""
        ),

      start_date:
        String(
          promotion.start_date ||
            ""
        ).slice(0, 10),

      end_date:
        String(
          promotion.end_date ||
            ""
        ).slice(0, 10),

      active:
        promotion.active === true ||
        promotion.active === 1 ||
        promotion.active === "true",
    });

    const linkedProducts =
      Array.isArray(
        promotion.products
      )
        ? promotion.products.map(
            (linked) => {
              const fullProduct =
                products.find(
                  (product) =>
                    Number(
                      product.id
                    ) ===
                    Number(
                      linked.product_id
                    )
                );

              return (
                fullProduct || {
                  id:
                    linked.product_id,
                  name:
                    linked.name,
                  barcode:
                    linked.barcode,
                  price:
                    linked.price,
                  category:
                    linked.category,
                }
              );
            }
          )
        : [];

    setSelectedProducts(
      linkedProducts
    );

    setProductSearch("");
    setFormError("");
    setSuccessMessage("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ======================================
  // DELETE
  // ======================================

  const deletePromotion = async (
    promotion
  ) => {
    const confirmed =
      window.confirm(
        `Delete multi-buy promotion?\n\n` +
          `${promotion.name}\n` +
          `${promotion.required_qty} for ${formatMoney(
            promotion.bundle_price
          )}\n\n` +
          `This action cannot be undone.`
      );

    if (!confirmed) {
      return;
    }

    try {
      const token =
        getAuthToken();

      await axios.delete(
        `${API_BASE_URL}/multi-buy-promotions/${promotion.id}`,
        {
          params: {
            shop_id: shopId,
          },

          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

      if (
        Number(
          editingPromotionId
        ) ===
        Number(promotion.id)
      ) {
        resetForm();
      }

      setSuccessMessage(
        "Multi-buy promotion deleted successfully."
      );

      await fetchPromotions();
    } catch (error) {
      console.error(
        "Delete multi-buy promotion error:",
        error.response?.data ||
          error.message
      );

      setPageError(
        error.response?.data?.error ||
          "Promotion could not be deleted."
      );
    }
  };

  // ======================================
  // UI
  // ======================================

  return (
    <div className="promotions-page">
      <div className="promotions-page-header">
        <div>
          <h1>
            Multi-Buy Promotions
          </h1>

          <p>
            Create offers where customers
            can mix eligible products, such
            as any 2 for €10.
          </p>
        </div>

        <div className="promotions-header-count">
          <span>Multi-Buy</span>

          <strong>
            {promotions.length}
          </strong>
        </div>
      </div>

      {successMessage && (
        <div className="promotion-success-message">
          ✓ {successMessage}
        </div>
      )}

      {pageError && (
        <div className="promotion-page-error">
          <span>
            {pageError}
          </span>

          <button
            type="button"
            onClick={() => {
              setPageError("");
              fetchProducts();
              fetchPromotions();
            }}
          >
            Try Again
          </button>
        </div>
      )}

      <section className="promotion-create-card">
        <div className="promotion-card-header">
          <div>
            <h2>
              {editingPromotionId
                ? "Edit Multi-Buy Promotion"
                : "Create Multi-Buy Promotion"}
            </h2>

            <p>
              Choose the products that can
              be mixed together in the
              offer.
            </p>
          </div>

          {editingPromotionId && (
            <button
              type="button"
              className="promotion-cancel-edit-btn"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          )}
        </div>

        <form
          className="promotion-form"
          onSubmit={savePromotion}
        >
          <div className="promotion-form-field">
            <label>
              Promotion Name{" "}
              <span>*</span>
            </label>

            <input
              type="text"
              placeholder="Example: VELO Any 2 for €10"
              value={form.name}
              onChange={(event) =>
                setForm(
                  (previous) => ({
                    ...previous,
                    name:
                      event.target
                        .value,
                  })
                )
              }
            />
          </div>

          <div className="promotion-form-field">
            <label>
              Required Quantity{" "}
              <span>*</span>
            </label>

            <input
              type="number"
              min="2"
              step="1"
              value={
                form.required_qty
              }
              onChange={(event) =>
                setForm(
                  (previous) => ({
                    ...previous,
                    required_qty:
                      event.target
                        .value,
                  })
                )
              }
            />
          </div>

          <div className="promotion-form-field">
            <label>
              Bundle Price (€){" "}
              <span>*</span>
            </label>

            <input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="10.00"
              value={
                form.bundle_price
              }
              onChange={(event) =>
                setForm(
                  (previous) => ({
                    ...previous,
                    bundle_price:
                      event.target
                        .value,
                  })
                )
              }
            />
          </div>

          <div className="promotion-form-field promotion-product-field">
            <label>
              Eligible Products{" "}
              <span>*</span>
            </label>

            <input
              type="text"
              placeholder="Search product name or barcode"
              value={
                productSearch
              }
              onChange={(event) =>
                setProductSearch(
                  event.target.value
                )
              }
            />

            {productSearch.trim() && (
              <div className="promotion-product-results">
                {loadingProducts ? (
                  <div className="promotion-product-empty">
                    Loading products...
                  </div>
                ) : filteredProducts.length >
                  0 ? (
                  filteredProducts.map(
                    (product) => (
                      <button
                        key={
                          product.id
                        }
                        type="button"
                        className="promotion-product-option"
                        onClick={() =>
                          addProduct(
                            product
                          )
                        }
                      >
                        <div>
                          <strong>
                            {product.name}
                          </strong>

                          <span>
                            Barcode:{" "}
                            {product.barcode ||
                              "-"}
                          </span>
                        </div>

                        <div className="promotion-product-option-price">
                          {formatMoney(
                            product.price
                          )}
                        </div>
                      </button>
                    )
                  )
                ) : (
                  <div className="promotion-product-empty">
                    No matching
                    products found.
                  </div>
                )}
              </div>
            )}

            {selectedProducts.length >
              0 && (
              <div
                style={{
                  marginTop: "12px",
                  display: "grid",
                  gap: "8px",
                }}
              >
                {selectedProducts.map(
                  (product) => (
                    <div
                      key={
                        product.id
                      }
                      className="selected-promotion-product"
                    >
                      <div>
                        <span>
                          Eligible Product
                        </span>

                        <strong>
                          {product.name}
                        </strong>

                        <small>
                          {product.barcode ||
                            "-"}
                        </small>
                      </div>

                      <div>
                        <strong>
                          {formatMoney(
                            product.price
                          )}
                        </strong>

                        <button
                          type="button"
                          className="promotion-delete-text-btn"
                          onClick={() =>
                            removeProduct(
                              product.id
                            )
                          }
                          style={{
                            marginLeft:
                              "10px",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          <div className="promotion-form-field">
            <label>
              Start Date{" "}
              <span>*</span>
            </label>

            <input
              type="date"
              value={
                form.start_date
              }
              onChange={(event) =>
                setForm(
                  (previous) => ({
                    ...previous,
                    start_date:
                      event.target
                        .value,
                  })
                )
              }
            />
          </div>

          <div className="promotion-form-field">
            <label>
              End Date{" "}
              <span>*</span>
            </label>

            <input
              type="date"
              min={
                form.start_date ||
                undefined
              }
              value={
                form.end_date
              }
              onChange={(event) =>
                setForm(
                  (previous) => ({
                    ...previous,
                    end_date:
                      event.target
                        .value,
                  })
                )
              }
            />
          </div>

          <div className="promotion-form-field promotion-active-field">
            <label>
              Promotion Status
            </label>

            <label className="promotion-switch-row">
              <input
                type="checkbox"
                checked={
                  form.active
                }
                onChange={(event) =>
                  setForm(
                    (previous) => ({
                      ...previous,
                      active:
                        event.target
                          .checked,
                    })
                  )
                }
              />

              <span className="promotion-switch-control" />

              <span>
                {form.active
                  ? "Promotion enabled"
                  : "Promotion disabled"}
              </span>
            </label>
          </div>

          {formError && (
            <div className="promotion-form-error">
              {formError}
            </div>
          )}

          <div className="promotion-form-actions">
            <button
              type="button"
              className="promotion-reset-btn"
              onClick={resetForm}
              disabled={saving}
            >
              Clear
            </button>

            <button
              type="submit"
              className="promotion-save-btn"
              disabled={saving}
            >
              {saving
                ? "Saving..."
                : editingPromotionId
                  ? "Save Changes"
                  : "Create Multi-Buy"}
            </button>
          </div>
        </form>
      </section>

      <section className="promotions-list-card">
        <div className="promotions-list-header">
          <div>
            <h2>
              Existing Multi-Buy Promotions
            </h2>

            <p>
              View and manage mix-and-match
              offers.
            </p>
          </div>

          <input
            type="text"
            className="promotion-list-search"
            placeholder="Search promotions..."
            value={
              promotionSearch
            }
            onChange={(event) =>
              setPromotionSearch(
                event.target.value
              )
            }
          />
        </div>

        {loadingPromotions ? (
          <div className="promotions-empty-state">
            Loading promotions...
          </div>
        ) : filteredPromotions.length ===
          0 ? (
          <div className="promotions-empty-state">
            <strong>
              No multi-buy promotions found
            </strong>
          </div>
        ) : (
          <div className="promotions-desktop-table-wrapper">
            <table className="promotions-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Offer</th>
                  <th>Products</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredPromotions.map(
                  (promotion) => {
                    const status =
                      normalizeStatus(
                        promotion
                      );

                    return (
                      <tr
                        key={
                          promotion.id
                        }
                      >
                        <td>
                          <strong>
                            {promotion.name}
                          </strong>
                        </td>

                        <td className="promotion-price-cell">
                          {
                            promotion.required_qty
                          }{" "}
                          for{" "}
                          {formatMoney(
                            promotion.bundle_price
                          )}
                        </td>

                        <td>
                          {Array.isArray(
                            promotion.products
                          )
                            ? promotion
                                .products
                                .map(
                                  (
                                    product
                                  ) =>
                                    product.name
                                )
                                .join(", ")
                            : "-"}
                        </td>

                        <td>
                          {formatDate(
                            promotion.start_date
                          )}
                        </td>

                        <td>
                          {formatDate(
                            promotion.end_date
                          )}
                        </td>

                        <td>
                          <span
                            className={`promotion-status-badge ${status}`}
                          >
                            {status}
                          </span>
                        </td>

                        <td>
                          <div className="promotion-row-actions">
                            <button
                              type="button"
                              className="promotion-edit-btn"
                              onClick={() =>
                                editPromotion(
                                  promotion
                                )
                              }
                            >
                              ✎
                            </button>

                            <button
                              type="button"
                              className="promotion-delete-btn"
                              onClick={() =>
                                deletePromotion(
                                  promotion
                                )
                              }
                            >
                              🗑
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default MultiBuyPromotionsPage;